import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { LineChart as MuiLineChart } from '@mui/x-charts/LineChart';
import KpiCard from './KpiCard';
import { getUncatchEndpointData } from '../utils/request';
import {
  VDR_CHART,
  vdrChartSx,
  vdrLegendLabelStyle,
  vdrOverlaySlotProps,
  vdrTooltipPopperSx
} from './vdrChartStyle';

type ActividadPayload = {
  kpis: {
    estudios: number;
    estudiosCompletados: number;
    estudiosConexionGd: number;
  };
  serieMensual: Array<{
    mes: string;
    capacidadAlojamiento: number;
    conexionGd: number;
  }>;
};

const PAYLOAD_INICIAL: ActividadPayload = {
  kpis: { estudios: 0, estudiosCompletados: 0, estudiosConexionGd: 0 },
  serieMensual: []
};

const formatearMesEsp = (yyyymm: string): string => {
  const match = yyyymm.match(/^(\d{4})-(\d{2})$/);
  if (!match) return yyyymm;
  const meses = [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic'
  ];
  const mIdx = parseInt(match[2], 10) - 1;
  if (mIdx < 0 || mIdx > 11) return yyyymm;
  return `${meses[mIdx]} ${match[1].slice(2)}`;
};

const PlataformaViva: FunctionComponent = () => {
  const [payload, setPayload] = useState<ActividadPayload>(PAYLOAD_INICIAL);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    const fetchData = async () => {
      try {
        const res = await getUncatchEndpointData({
          endpoint: 'penetracion/actividad'
        });
        if (cancelado) return;
        setPayload(res as ActividadPayload);
        setError(null);
      } catch (err) {
        if (cancelado) return;
        console.error(err);
        setError('No se pudo cargar la actividad de la plataforma.');
      }
    };
    fetchData();
    return () => {
      cancelado = true;
    };
  }, []);

  const fechas = useMemo(
    () => payload.serieMensual.map((p) => formatearMesEsp(p.mes)),
    [payload]
  );
  const valoresCapacidad = useMemo(
    () => payload.serieMensual.map((p) => p.capacidadAlojamiento),
    [payload]
  );
  const valoresConexionGd = useMemo(
    () => payload.serieMensual.map((p) => p.conexionGd),
    [payload]
  );

  const tickInterval = useMemo(() => {
    if (fechas.length === 0) return undefined;
    const step = Math.max(1, Math.ceil(fechas.length / 6));
    return (_: unknown, idx: number) => idx % step === 0;
  }, [fechas]);

  if (error) {
    return <div className='kpi-card-status kpi-card-status--error'>{error}</div>;
  }

  return (
    <div className='bloque1-actividad'>
      <div className='bloque1-actividad-kpis'>
        <KpiCard
          valor={payload.kpis.estudios}
          label='Estudios ejecutados'
          variante='compacto'
        />
        <KpiCard
          valor={payload.kpis.estudiosCompletados}
          label='Estudios completados'
          variante='compacto'
        />
        <KpiCard
          valor={payload.kpis.estudiosConexionGd}
          label='Estudios de conexión GD'
          variante='compacto'
        />
      </div>

      <div className='bloque1-actividad-spark'>
        <h5 className='bloque1-spark-titulo'>Estudios por mes (últimos 12 meses)</h5>
        {fechas.length > 0 ? (
          <MuiLineChart
            xAxis={[{ data: fechas, scaleType: 'band', tickInterval }]}
            series={[
              {
                data: valoresCapacidad,
                label: 'Capacidad de alojamiento',
                area: true,
                stack: 'total',
                showMark: false,
                color: VDR_CHART.ink4,
                valueFormatter: (v) => `${Math.round(v ?? 0)} estudios`
              },
              {
                data: valoresConexionGd,
                label: 'Conexión GD',
                area: true,
                stack: 'total',
                showMark: false,
                color: VDR_CHART.cyanDark,
                valueFormatter: (v) => `${Math.round(v ?? 0)} estudios`
              }
            ]}
            height={200}
            margin={{ top: 16, bottom: 64, left: 40, right: 12 }}
            grid={{ horizontal: true }}
            sx={vdrChartSx}
            slotProps={{
              legend: {
                direction: 'row',
                position: { vertical: 'bottom', horizontal: 'middle' },
                padding: { top: 4, bottom: 6, left: 8, right: 8 },
                itemGap: 20,
                markGap: 8,
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                labelStyle: vdrLegendLabelStyle
              },
              popper: { sx: vdrTooltipPopperSx },
              ...vdrOverlaySlotProps
            }}
          />
        ) : (
          <div className='bloque1-spark-vacio'>Sin actividad registrada en los últimos 12 meses.</div>
        )}
      </div>
    </div>
  );
};

export default PlataformaViva;
